import { useEffect, useRef, useState } from "react";
import StatusBadge from "./StatusBadge";
import { ShieldCheck, AlertOctagon, MoreVertical, Pencil, Trash2, ChevronDown } from "lucide-react";

const borderByStatus = {
  valid: "border-l-emerald-500",
  expiring: "border-l-amber-500",
  expired: "border-l-red-500",
};

const fmtDate = (d) => {
  try {
    const [y, m, day] = d.split("-");
    return `${m}/${day}/${y}`;
  } catch {
    return d;
  }
};

export default function CredentialCard({ credential, testid, onEdit, onDelete }) {
  const verif = credential.verification === "ProCred Verified";
  const [menuOpen, setMenuOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const showMenu = Boolean(onEdit || onDelete);

  return (
    <div
      data-testid={testid}
      className={`relative border border-slate-200 ${borderByStatus[credential.status] || "border-l-slate-300"} border-l-[6px] bg-white p-5 transition-shadow hover:shadow-md`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="font-display text-base font-bold leading-snug text-[#0B1221]">
            {credential.name}
          </h3>
          <p className="mt-0.5 text-sm text-slate-600">{credential.issuer}</p>
        </div>
        <div className="flex items-start gap-2">
          <StatusBadge status={credential.status} testid={`${testid}-status`} />
          {showMenu && (
            <div className="relative" ref={wrapRef}>
              <button
                type="button"
                data-testid={`${testid}-menu`}
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="Open credential menu"
                className="flex h-7 w-7 items-center justify-center border border-transparent text-slate-500 hover:border-slate-200 hover:bg-slate-50 hover:text-[#0B1221]"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {menuOpen && (
                <div
                  data-testid={`${testid}-menu-popover`}
                  className="absolute right-0 z-20 mt-1 w-40 border border-slate-200 bg-white py-1 shadow-md"
                >
                  {onEdit && (
                    <button
                      type="button"
                      data-testid={`${testid}-menu-edit`}
                      onClick={() => {
                        setMenuOpen(false);
                        onEdit(credential);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#0B1221] hover:bg-slate-50"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      data-testid={`${testid}-menu-delete`}
                      onClick={() => {
                        setMenuOpen(false);
                        onDelete(credential);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Expires
          </div>
          <div className="mt-0.5 text-sm font-semibold text-[#0B1221]">
            {fmtDate(credential.expires)}
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Verification
          </div>
          <div
            className={`mt-0.5 inline-flex items-center gap-1 text-sm font-semibold ${
              verif ? "text-[#0B1221]" : "text-red-700"
            }`}
          >
            {verif ? (
              <ShieldCheck className="h-3.5 w-3.5 text-[#FF5A1F]" strokeWidth={2.5} />
            ) : (
              <AlertOctagon className="h-3.5 w-3.5 text-red-600" strokeWidth={2.5} />
            )}
            {credential.verification}
          </div>
        </div>
      </div>

      <button
        type="button"
        data-testid={`${testid}-toggle-details`}
        onClick={() => setDetailsOpen((v) => !v)}
        className="mt-4 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#0B1221]"
      >
        <ChevronDown className={`h-3 w-3 transition-transform ${detailsOpen ? "rotate-180" : ""}`} strokeWidth={2.5} />
        {detailsOpen ? "Hide details" : "View details"}
      </button>

      {detailsOpen && (
        <dl
          data-testid={`${testid}-details`}
          className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-slate-100 pt-3 text-xs"
        >
          {[
            ["Credential Type", credential.credential_type],
            ["Trade", credential.trade],
            ["License / Cert #", credential.license_number],
            ["State / Jurisdiction", credential.state_jurisdiction],
            ["Issue Date", credential.issue_date ? fmtDate(credential.issue_date) : null],
            ["Expiration Date", fmtDate(credential.expires)],
            ["Supporting Document", credential.supporting_document],
            ["Verification Source", credential.verification_source],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{k}</dt>
              <dd className="mt-0.5 text-sm text-[#0B1221]">{v || <span className="text-slate-400">—</span>}</dd>
            </div>
          ))}
          {credential.verification_notes && (
            <div className="col-span-2">
              <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Verification Notes</dt>
              <dd className="mt-0.5 text-sm text-[#0B1221]">{credential.verification_notes}</dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}
